import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { CommonModule } from '@angular/common'
import { MatButtonModule } from '@angular/material/button'
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field'
import { MatInput, MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatIconModule } from '@angular/material/icon'
import { MatTableModule } from '@angular/material/table'
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker'
import { MatNativeDateModule } from '@angular/material/core'
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatDividerModule } from '@angular/material/divider'
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { MatButtonToggleModule } from '@angular/material/button-toggle'
import { MatTooltipModule } from '@angular/material/tooltip'
import { Subscription } from 'rxjs'
import moment from 'moment/moment'

import { PassengerService } from '../../../core/services/passenger/passenger.service'
import { StretchTypeService } from '../../../core/services/stretch/stretch-type/stretch-type.service'
import { ViaCepService } from '../../../core/services/address/via-cep/via-cep.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { DocumentTypeService } from '../../../core/services/document/document-type/document-type.service'
import { ModalGoogleMapsComponent } from '../../address/modal-google-maps/modal-google-maps.component'

const TIPO_CONFIGS: Record<number, { origemTipo: string; destinoTipo: string; toggleOrigem: boolean; toggleDestino: boolean }> = {
  1: { origemTipo: 'AEROPORTO', destinoTipo: 'ENDERECO', toggleOrigem: true,  toggleDestino: true  },
  2: { origemTipo: 'ENDERECO',  destinoTipo: 'AEROPORTO', toggleOrigem: true,  toggleDestino: true  },
  3: { origemTipo: 'ENDERECO',  destinoTipo: 'ENDERECO',  toggleOrigem: false, toggleDestino: false },
  4: { origemTipo: 'ENDERECO',  destinoTipo: 'ENDERECO',  toggleOrigem: false, toggleDestino: false },
  5: { origemTipo: 'ENDERECO',  destinoTipo: 'AEROPORTO', toggleOrigem: false, toggleDestino: false },
  6: { origemTipo: 'AEROPORTO', destinoTipo: 'ENDERECO',  toggleOrigem: false, toggleDestino: false },
  7: { origemTipo: 'ENDERECO',  destinoTipo: 'ENDERECO',  toggleOrigem: true,  toggleDestino: true  },
}

@Component({
  selector: 'passengers-edit-logistics',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormField, MatInput, MatLabel,
    MatSelectModule,
    MatIconModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgxMaskDirective,
    MatDividerModule,
    MatProgressSpinner,
    MatCard, MatCardTitle, MatCardContent,
    MatSlideToggle,
    MatButtonToggleModule,
    MatTooltipModule,
  ],
  templateUrl: './passengers-edit-logistics.component.html',
  styleUrl: './passengers-edit-logistics.component.scss',
  providers: [provideNgxMask({ dropSpecialCharacters: false, validation: false })],
})
export class PassengerEditLogisticsComponent implements OnInit, OnDestroy {
  @Output() response = new EventEmitter<any>()
  @ViewChild('dataPicker') datepickerData: MatDatepicker<any> | undefined
  @ViewChild('vooDataPicker') datepickerVooData: MatDatepicker<any> | undefined

  form!: FormGroup
  loading = false
  tipoTrechoList: any[] = []
  cellphoneMask = '(00) 0000-0000||(00) 00000-0000'

  private sub = new Subscription()
  private isInitializing = false

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<PassengerEditLogisticsComponent>,
    private dialog: MatDialog,
    private passengerService: PassengerService,
    private viaCepService: ViaCepService,
    private stretchTypeService: StretchTypeService,
    private documentTypeService: DocumentTypeService,
    private snackbar: SnackbarCustomService,
  ) {}

  ngOnInit(): void {
    this.createForm()
    this.loadTipoTrecho()
    this.sub.add(
      this.form.get('tipoTrechoId')!.valueChanges.subscribe(v => {
        if (!this.isInitializing) this.onTipoChange(v)
      }),
    )
    if (this.data.edit && this.data.item) {
      this.isInitializing = true
      const tipoId = this.data.item.tipoTrechoId ?? this.data.item.tipo_trecho_id ?? null
      const patch = this.mapLegacyToForm(this.data.item, tipoId)
      this.form.patchValue(patch)
      this.isInitializing = false
    }
  }

  ngOnDestroy(): void { this.sub.unsubscribe() }

  private createForm(): void {
    this.form = this.fb.group({
      nome:             ['', Validators.required],
      documento:        [''],
      contato:          [''],
      tipoTrechoId:     [null, Validators.required],
      data:             ['', Validators.required],
      hora:             [''],
      speaker:          ['NÃO'],
      observacoes:      [''],
      origemTipo:       [null],
      origemAeroSigla:  [''],
      origemLogradouro: [''],
      origemNumero:     [''],
      origemBairro:     [''],
      origemCidade:     [''],
      origemCep:           [''],
      origemComplemento:   [''],
      destinoTipo:         [null],
      destinoAeroSigla:    [''],
      destinoLogradouro:   [''],
      destinoNumero:       [''],
      destinoBairro:       [''],
      destinoCidade:       [''],
      destinoCep:          [''],
      destinoComplemento:  [''],
      companhia:        [''],
      voo:              [''],
      vooData:          [''],
      vooHora:          [''],
    })
  }

  private loadTipoTrecho(): void {
    this.stretchTypeService.listStretchType().subscribe({
      next: (res: any) => { this.tipoTrechoList = res.data || [] },
    })
  }

  private onTipoChange(tipoId: number | null): void {
    if (!tipoId) return
    const cfg = TIPO_CONFIGS[tipoId]
    if (!cfg) return
    if (!this.form.get('origemTipo')?.value) {
      this.form.patchValue({
        origemTipo:  cfg.origemTipo  || null,
        destinoTipo: cfg.destinoTipo || null,
      })
    }
  }

  private mapLegacyToForm(item: any, tipoId: number | null): any {
    const parseDate = (d: string | null | undefined): Date | null => {
      if (!d) return null
      const m = d.includes('/') ? moment(d, 'DD/MM/YYYY') : moment(d, 'YYYY-MM-DD')
      return m.isValid() ? m.toDate() : null
    }

    const parseSpeaker = (s: string | null | undefined): string =>
      (s === 'S' || s === 'SIM') ? 'SIM' : 'NÃO'

    const patch: any = {
      nome:         item.nome      || '',
      documento:    item.documento || '',
      contato:      item.contato   || '',
      tipoTrechoId: tipoId,
      data:         parseDate(item.data),
      hora:         item.hora  ? item.hora.substring(0, 5)  : '',
      speaker:      parseSpeaker(item.speaker),
      observacoes:  item.observacoes || '',
      companhia:    item.companhia   || '',
      voo:          item.voo ?? item.numeroVoo ?? '',
      vooData:      parseDate(item.vooData),
      vooHora:      item.vooHora ? item.vooHora.substring(0, 5) : '',
    }

    // New API format: origemTipo / destinoTipo returned directly on item
    if (item.origemTipo) {
      patch.origemTipo         = item.origemTipo
      patch.origemAeroSigla    = item.origemAeroSigla    || ''
      patch.origemLogradouro   = item.origemLogradouro   || ''
      patch.origemNumero       = item.origemNumero       || ''
      patch.origemComplemento  = item.origemComplemento  || ''
      patch.origemBairro       = item.origemBairro       || ''
      patch.origemCidade       = item.origemCidade       || ''
      patch.origemCep          = item.origemCep          || ''
    }

    if (item.destinoTipo) {
      patch.destinoTipo        = item.destinoTipo
      patch.destinoAeroSigla   = item.destinoAeroSigla   || ''
      patch.destinoLogradouro  = item.destinoLogradouro  || ''
      patch.destinoNumero      = item.destinoNumero      || ''
      patch.destinoComplemento = item.destinoComplemento || ''
      patch.destinoBairro      = item.destinoBairro      || ''
      patch.destinoCidade      = item.destinoCidade      || ''
      patch.destinoCep         = item.destinoCep         || ''
    }

    // Legacy fallback: old field names (pre-new-API responses)
    if (!item.origemTipo && !item.destinoTipo && tipoId) {
      const cfg = TIPO_CONFIGS[tipoId]
      if (cfg) {
        patch.origemTipo  = cfg.origemTipo  || null
        patch.destinoTipo = cfg.destinoTipo || null
      }

      const aeroSigla = item.destinoOrigemAero || item.origemAeroSigla || item.destinoAeroSigla || ''

      switch (tipoId) {
        case 1:
          patch.origemAeroSigla   = aeroSigla
          patch.destinoLogradouro = item.logradouroAero || ''
          patch.destinoNumero     = item.numeroAero     || ''
          patch.destinoBairro     = item.bairroAero     || ''
          patch.destinoCidade     = item.cidadeAero     || ''
          patch.destinoCep        = item.cepAero        || ''
          break
        case 2:
          patch.origemLogradouro  = item.logradouroAero || ''
          patch.origemNumero      = item.numeroAero     || ''
          patch.origemBairro      = item.bairroAero     || ''
          patch.origemCidade      = item.cidadeAero     || ''
          patch.origemCep         = item.cepAero        || ''
          patch.destinoAeroSigla  = aeroSigla
          break
        case 3:
        case 4:
          patch.origemLogradouro  = item.logradouroPap || ''
          patch.origemNumero      = item.numeroPap     || ''
          patch.origemBairro      = item.bairroPap     || ''
          patch.origemCidade      = item.cidadePap     || ''
          patch.origemCep         = item.cepPap        || ''
          break
        case 5:
          patch.origemLogradouro  = item.logradouroPap || ''
          patch.origemNumero      = item.numeroPap     || ''
          patch.origemBairro      = item.bairroPap     || ''
          patch.origemCidade      = item.cidadePap     || ''
          patch.origemCep         = item.cepPap        || ''
          patch.destinoAeroSigla  = aeroSigla
          break
        case 6:
          patch.origemAeroSigla   = aeroSigla
          patch.destinoLogradouro = item.logradouroPap || ''
          patch.destinoNumero     = item.numeroPap     || ''
          patch.destinoBairro     = item.bairroPap     || ''
          patch.destinoCidade     = item.cidadePap     || ''
          patch.destinoCep        = item.cepPap        || ''
          break
      }
    }

    return patch
  }

  get tipoId(): number | null { return this.form.get('tipoTrechoId')?.value ?? null }
  get origemTipo(): string    { return this.form.get('origemTipo')?.value ?? '' }
  get destinoTipo(): string   { return this.form.get('destinoTipo')?.value ?? '' }
  get showOrigem(): boolean   { return !!this.tipoId }
  get showDestino(): boolean  { return !!this.tipoId }
  get showAereo(): boolean    { return this.origemTipo === 'AEROPORTO' || this.destinoTipo === 'AEROPORTO' }
  get toggleOrigem(): boolean { return !!this.tipoId && !!(TIPO_CONFIGS[this.tipoId]?.toggleOrigem) }
  get toggleDestino(): boolean{ return !!this.tipoId && !!(TIPO_CONFIGS[this.tipoId]?.toggleDestino) }

  setOrigemTipo(val: string): void  { this.form.get('origemTipo')?.setValue(val) }
  setDestinoTipo(val: string): void { this.form.get('destinoTipo')?.setValue(val) }

  openDatepicker(picker: 'data' | 'vooData'): void {
    if (picker === 'data') this.datepickerData?.open()
    if (picker === 'vooData') this.datepickerVooData?.open()
  }

  openAddressSearch(side: 'origem' | 'destino'): void {
    const ref = this.dialog.open(ModalGoogleMapsComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '900px',
      width: '900px',
    })
    ref.componentInstance.response.subscribe((data: any) => {
      if (data?.insert && data.model) {
        const m = data.model
        this.form.patchValue({
          [`${side}Logradouro`]: m.logradouro || '',
          [`${side}Numero`]:     m.numero     || '',
          [`${side}Bairro`]:     m.bairro     || '',
          [`${side}Cidade`]:     m.cidade     || '',
          [`${side}Cep`]:        m.cep        || '',
        })
      }
    })
  }

  lookupCepOrigem(): void {
    this.lookupCep('origemCep', 'origemLogradouro', 'origemNumero', 'origemBairro', 'origemCidade')
  }
  lookupCepDestino(): void {
    this.lookupCep('destinoCep', 'destinoLogradouro', 'destinoNumero', 'destinoBairro', 'destinoCidade')
  }

  private lookupCep(cepKey: string, logKey: string, numKey: string, baiKey: string, cidKey: string): void {
    const cep = (this.form.get(cepKey)?.value ?? '').replace(/\D/g, '')
    if (cep.length < 8) return
    this.viaCepService.getAdressbyPostalCode(cep).subscribe({
      next: (res: any) => {
        if (!res.erro) {
          this.form.patchValue({ [logKey]: res.logradouro, [numKey]: '', [baiKey]: res.bairro, [cidKey]: res.localidade })
        }
      },
    })
  }

  private formatDate(d: any): string {
    if (!d) return ''
    if (d instanceof Date) return moment(d).format('DD/MM/YYYY')
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [y, m, day] = d.split('-')
      return `${day}/${m}/${y}`
    }
    return d
  }

  private buildPayload(): any {
    const v = this.form.value

    const payload: any = {
      id:           this.data.item?.id,
      nome:         v.nome,
      documento:    v.documento || '',
      contato:      v.contato   || '',
      tipoTrechoId: v.tipoTrechoId,
      data:         this.formatDate(v.data),
      hora:         v.hora  || '',
      contratoId:   this.data.idContrato,
      speaker:      v.speaker === 'SIM' ? 'S' : 'N',
      observacoes:  v.observacoes || '',
    }

    if (this.showOrigem && v.origemTipo) {
      payload.origemTipo = v.origemTipo
      if (v.origemTipo === 'AEROPORTO') {
        payload.origemAeroSigla = (v.origemAeroSigla || '').toUpperCase()
      }
      payload.origemLogradouro   = v.origemLogradouro   || ''
      payload.origemNumero       = v.origemNumero       || ''
      payload.origemComplemento  = v.origemComplemento  || ''
      payload.origemBairro       = v.origemBairro       || ''
      payload.origemCidade       = v.origemCidade       || ''
      payload.origemCep          = v.origemCep          || ''
    }

    if (this.showDestino && v.destinoTipo) {
      payload.destinoTipo = v.destinoTipo
      if (v.destinoTipo === 'AEROPORTO') {
        payload.destinoAeroSigla = (v.destinoAeroSigla || '').toUpperCase()
      }
      payload.destinoLogradouro  = v.destinoLogradouro  || ''
      payload.destinoNumero      = v.destinoNumero      || ''
      payload.destinoComplemento = v.destinoComplemento || ''
      payload.destinoBairro      = v.destinoBairro      || ''
      payload.destinoCidade      = v.destinoCidade      || ''
      payload.destinoCep         = v.destinoCep         || ''
    }

    if (this.showAereo) {
      payload.companhia = v.companhia || ''
      payload.voo       = v.voo       || ''
      payload.vooData   = this.formatDate(v.vooData)
      payload.vooHora   = v.vooHora   || ''
    }

    return payload
  }

  addItemToList(): void {
    if (this.form.invalid) return
    const model = this.buildPayload()
    this.loading = true
    this.passengerService.editPassenger(model).subscribe({
      next: () => {
        this.snackbar.success('Passageiro editado com sucesso')
        this.response.emit({ action: 'edit' })
        this.dialogRef.close({ success: true })
      },
      error: () => {
        this.snackbar.error('Erro ao editar passageiro.')
        this.loading = false
        this.dialogRef.close({ success: false })
      },
      complete: () => { this.loading = false },
    })
  }

  closeModal(): void { this.dialogRef.close() }
}
